import { Component } from '@theme/component';

export class BbdProductMediaCarousel extends Component {
  #current = 0;

  #thumbnailsOpen = false;

  // Drag/swipe state
  #pointerId = null;
  #startX = 0;
  #startY = 0;
  #dragged = false;
  #tracking = false;

  connectedCallback() {
    super.connectedCallback();
    this.#updateControlsVisibility();

    // Allow vertical page scrolling while we handle horizontal swipes
    this.style.touchAction = 'pan-y';
    // Enable swipe/drag gestures on the carousel root
    this.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
    this.addEventListener('pointermove', this.#onPointerMove, { passive: false });
    this.addEventListener('pointerup', this.#onPointerUp, { passive: true });
    this.addEventListener('pointercancel', this.#onPointerCancel, { passive: true });
    this.addEventListener('lostpointercapture', this.#onPointerCancel, { passive: true });
    // Capture clicks to prevent click-through after a drag
    this.addEventListener('click', this.#onClickCapture, true);
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#updateControlsVisibility();
  }

  #updateControlsVisibility() {
    if (this.refs.model && this.#current === 0) {
      this.refs.thumbnails?.classList.remove('open');
      this.#thumbnailsOpen = false;
    } else if (this.refs.model && this.#current !== 0) {
      this.refs.thumbnails?.classList.add('open');
      this.#thumbnailsOpen = true;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerCancel);
    this.removeEventListener('lostpointercapture', this.#onPointerCancel);
    this.removeEventListener('click', this.#onClickCapture, true);
  }

  get current() {
    return this.#current;
  }

  setCurrent(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      const isVisible = index === relativeIndex;
      slide.setAttribute('aria-hidden', `${!isVisible}`);

      // Pause media if the slide is not visible
      if (!isVisible) {
        const deferredMedias = slide.querySelectorAll('deferred-media, product-model');
        deferredMedias.forEach((media) => {
          if (typeof media.pauseMedia === 'function') {
            media.pauseMedia();
          }
        });
      }
    });

    this.refs.thumbnails?.style.setProperty('--active-thumbnail', `${relativeIndex + 1}`);

    this.#updateControlsVisibility();
  }

  openThumbnails() {
    if (this.refs.thumbnails && !this.refs.thumbnails.classList.contains('open')) {
      this.refs.thumbnails.classList.add('open');
      this.#thumbnailsOpen = true;
    }
  }

  closeThumbnails() {
    if (this.refs.thumbnails) {
      this.refs.thumbnails?.style.setProperty('--active-thumbnail', `${(this.refs.slides ?? []).length}`);
      this.refs.thumbnails.classList.remove('open');
      this.#thumbnailsOpen = false;
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    this.setCurrent(index);

    if (this.refs.model && index === 0) {
      this.closeThumbnails();
      return;
    }

    if (index === 0 && !this.#thumbnailsOpen) {
      this.closeThumbnails();
      return;
    }

    this.openThumbnails();
  }

  next() {
    const nextIndex = this.#current + 1;
    this.setCurrent(nextIndex);
  }

  previous() {
    const prevIndex = this.#current - 1;
    this.setCurrent(prevIndex);
  }

  // Private: pointer/click handlers for swipe navigation
  #onClickCapture = (event) => {
    if (this.#dragged) {
      event.preventDefault();
      event.stopPropagation();
      // Reset dragged flag after suppressing the click
      this.#dragged = false;
    }
  };

  #onPointerDown = (event) => {
    // Only handle primary button/touch
    if (event.isPrimary === false) return;
    const len = (this.refs.slides ?? []).length;
    if (len <= 1) return;

    // If the first slide is a 3D model and we're currently on it, do not start swipe tracking
    // This allows the model to handle its own gestures and prevents accidental navigation
    let relativeIndex = this.#current % len;
    if (relativeIndex < 0) relativeIndex += len;
    if (this.refs.model && relativeIndex === 0) return;

    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#tracking = true;
  };

  #onPointerMove = (event) => {
    if (!this.#tracking || this.#pointerId !== event.pointerId) return;

    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;

    // Start horizontal drag only when horizontal dominates
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    if (!this.#dragged && absDx > 6 && absDx > absDy) {
      // We're engaging a horizontal drag; prevent scroll/jitter
      event.preventDefault();
      this.#dragged = true;
      return;
    }

    if (this.#dragged) {
      // While dragging, prevent native behaviors
      event.preventDefault();
    }
  };

  #onPointerUp = (event) => {
    if (!this.#tracking || this.#pointerId !== event.pointerId) return;

    const dx = event.clientX - this.#startX;
    const absDx = Math.abs(dx);

    const width = this.clientWidth || 1;
    const threshold = Math.max(60, width * 0.15);

    if (this.#dragged && absDx >= threshold) {
      // Decide direction: swipe left -> next, swipe right -> previous
      if (dx < 0) this.next();
      else this.previous();
    }

    // Reset gesture state; keep #dragged true to suppress click once in capture
    const wasDragged = this.#dragged;
    this.#pointerId = null;
    this.#startX = 0;
    this.#startY = 0;
    this.#tracking = false;
    // If we didn't cross threshold, clear dragged so click will proceed
    if (!wasDragged || absDx < threshold) {
      this.#dragged = false;
    }
  };

  #onPointerCancel = (event) => {
    if (this.#pointerId !== null && this.#pointerId === event.pointerId) {
      this.#pointerId = null;
      this.#tracking = false;
      this.#dragged = false;
    }
  };
}

if (!customElements.get('bbd-product-media-carousel')) {
  customElements.define('bbd-product-media-carousel', BbdProductMediaCarousel);
}
